import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { lessonMarkdownComponents } from "@/lib/md";
import { contentSections, type ContentSectionId } from "@/content/registry";

interface Props {
  /** Stable content id — see src/content/registry.ts. Type-checked, so an
   *  unregistered id is a compile error. */
  id: ContentSectionId;
  /** Optional extra classes on the outer card (e.g. spacing overrides). */
  className?: string;
}

/** The single, shared way to render editable page content across the site.
 *
 *  Resolves the section by id and renders it in the one content design with the
 *  standard sanitized markdown pipeline. The id indirection (vs. inlining copy)
 *  is what lets us later resolve from admin-edited storage without touching any
 *  call site — this component becomes the one place that changes. */
export function ContentSection({ id, className }: Props): JSX.Element {
  const markdown = contentSections[id];
  return (
    <section
      className={`mb-8 rounded-card border border-border bg-card p-6 sm:p-8 shadow-sm${
        className ? ` ${className}` : ""
      }`}
    >
      <div className="text-muted-foreground">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSanitize]}
          components={lessonMarkdownComponents}
        >
          {markdown}
        </ReactMarkdown>
      </div>
    </section>
  );
}
